import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ACTIVE_HIPAA_VERSION, getHipaaBody } from '../legal/hipaa';
import type { Profile } from '../types/profile';
import { renderTemplate } from '../utils/renderTemplate';
import { useToast } from '../components/context/ToastContext';
import SignaturePad from '../components/SignaturePad';
import { generateHipaaPdf } from '../utils/generateHipaaPdf';
import { ArrowLeft } from 'lucide-react';
import { trackHipaaSigned } from '../lib/intercom';
import { phHipaaSigned } from '../lib/posthog';
import '@styles/pages/hipaa.css';

export default function Hipaa() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/add-bill';
  const { showToast } = useToast();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toLocaleDateString('en-US');

  /* ---------------------------
     LOAD USER + PROFILE
  ---------------------------- */
  useEffect(() => {
    const load = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate('/signup');
        return;
      }

      // Already signed current version for self?
      const { data: existing } = await supabase
        .from('hipaa_consents')
        .select('id')
        .eq('user_id', user.id)
        .eq('consent_version', ACTIVE_HIPAA_VERSION)
        .is('family_member_id', null)
        .limit(1)
        .maybeSingle();

      if (existing) {
        navigate(returnTo);
        return;
      }

      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      setProfile(profileData);
    };

    load();
  }, [navigate, returnTo]);

  if (!profile) return null;

  /* ---------------------------
     CONSENT TEXT
  ---------------------------- */
  const fullName = `${profile.first_name} ${profile.last_name}`;
  const consentText = renderTemplate(getHipaaBody('self'), {
    full_name: fullName,
    date_of_birth: profile.date_of_birth ?? '',
    printed_name: fullName,
    signature: '[Signed electronically]',
    today,
  });

  /* ---------------------------
     SUBMIT
  ---------------------------- */
  const handleSubmit = async () => {
    if (!accepted) {
      setError('You must accept the HIPAA Release Authorization');
      return;
    }

    if (!signatureData) {
      setError('Please draw your signature above');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      /* Refresh the session so the JWT is valid for RLS checks */
      const { data: sessionData, error: sessionError } =
        await supabase.auth.refreshSession();

      if (sessionError || !sessionData.session) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        return;
      }

      const user = sessionData.session.user;

      /* ---- 1. Generate signed PDF ---- */
      const pdfBlob = generateHipaaPdf({
        consentText,
        signatureDataUrl: signatureData,
        fullName,
        dateOfBirth: profile!.date_of_birth ?? '',
        dateSigned: today,
        consentVersion: ACTIVE_HIPAA_VERSION,
        signerRelationship: 'self',
      });

      /* ---- 2. Upload PDF to Supabase Storage ---- */
      const fileName = `${user.id}/${ACTIVE_HIPAA_VERSION}-${Date.now()}.pdf`;

      const { error: uploadError } = await supabase.storage
        .from('hipaa-consents')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
        });

      if (uploadError) {
        setError(uploadError.message);
        setLoading(false);
        return;
      }

      /* ---- 3. Save consent record with PDF path ---- */
      const { error: insertError } = await supabase
        .from('hipaa_consents')
        .insert({
          user_id: user.id,
          consent_version: ACTIVE_HIPAA_VERSION,
          consent_text: consentText,
          signed_name: fullName,
          signature_method: 'drawn',
          signed_pdf_path: fileName,
          user_agent: navigator.userAgent,
          signer_relationship: 'self',
        });

      if (insertError) {
        setError(insertError.message);
        setLoading(false);
        return;
      }

      showToast('HIPAA authorization complete!');
      trackHipaaSigned();
      // 2026-08-08: was Intercom-only — see the note in Onboarding.tsx.
      phHipaaSigned();
      navigate(returnTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------
     VALIDATION
  ---------------------------- */
  const canSubmit = accepted && !!signatureData;

  /* ---------------------------
     UI
  ---------------------------- */
  return (
    <div className="hipaa-container">
      <header className="auth-header">
        <div className="logo" aria-label="Mediloop">
          <img
            src="/images/logos/tail-logo.png"
            alt="Mediloop"
            className="logo-img"
            width={36}
            height={36}
          />
        </div>
      </header>

      <main className="hipaa-card">
        <div className="hipaa-eyebrow">  Authorization required  </div>
        <h1>HIPAA Release <span className="italic">Authorization.</span></h1>
        <p className="subtitle">
          Please review and sign the authorization below. This lets Agent Loop
          speak with your providers on your behalf, the same form any patient
          advocate would ask you to sign.
        </p>

        <div className="hipaa-content">
          <pre className="hipaa-box">{consentText}</pre>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              I have read and agree to the HIPAA Release Authorization
            </span>
          </label>

          <SignaturePad onSignatureChange={setSignatureData} />

          {error && <p className="form-error">{error}</p>}

          <button
            className="primary"
            onClick={handleSubmit}
            disabled={loading || !canSubmit}
          >
            {loading ? 'Saving…' : 'Sign & Continue'}
          </button>

          <button
            type="button"
            className="hipaa-back"
            onClick={() => navigate('/')}
            disabled={loading}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>

        {/* ---- TRUST SIGNAL ---- */}
        <div className="trust-row">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Your information is encrypted and HIPAA-protected</span>
        </div>
      </main>
    </div>
  );
}
